import React from "react";

// 1. Standard Page Imports
import PublicPage from "../pages/PublicPage";
import RegisterPage from "../auth/pre/SEC_02_02_RegisterController";
import SubscribePage from "../pages/SubscribePage";
import RegisteredDashboard from "../components/restricted/RegisteredDashboard";
import SignInPage from "../auth/pre/SEC_02_01_SignInController";
import VerifyEmailPage from "../auth/pages/VerifyEmailPage";
import ResetPasswordPage from "../auth/pages/ResetPasswordPage";

// 2. Security Guards
import { RequireAuth } from "../auth";
import PostSignInAccessGuard from "../auth/registered/SEC_03_01_PostSignInAccessGuard";
import RolePermissionGuard from "../auth/registered/SEC_03_02_RolePermissionGuard"; 

// 3. Post-Login Handoff
import SEC_03_00_PostLoginHandoff from "../auth/registered/SEC_03_00_PostLoginHandoff";

// --- 5. SEC_03 USER MANAGEMENT (NEW REFACTOR & LEGACY) ---
// 🎯 NEW ORCHESTRATOR
import SEC_03_UserManagementMainView from "../registered/site-admin/SEC_03_Usermanagement/SEC_03_UserManagementMainView";
// 🟡 LEGACY COMPONENTS (Preserved for safety)
import UserRoleAssignmentController from "../registered/site-admin/components/SEC_03_UserRoleAssignmentController";
import UserRoleAssignmentView from "../registered/site-admin/components/SEC_03_UserRoleAssignmentView";

import DataEntryWorkspace from "../components/restricted/data-entry/DataEntryWorkspace";

// --- SUSTINA & ADMIN HUB IMPORTS ---
import SiteAdminDashboard from "../registered/dashboard/SiteAdminDashboard";
import WorkflowRefreshESG from "../registered/site-admin/components/workflow_REFRESH_ESG_Taxonomy";
import SustinaTaxonomyView from "../registered/site-admin/components/SustinaTaxonomyView";
import BatchMonitorView from "../registered/upload/components/BatchMonitor/BatchMonitorView";
import WorkflowXls2SchemaController from "../registered/site-admin/components/Workflow-esg/Workflow_XLS_2_Schema_Controller";
import WorkflowHub from "../registered/site-admin/components/Workflow-esg/Workflow_Hub"; 

// 🎯 AUTHORITY: Dedicated S3 Physical Inventory View
import S3MonitorView from "../registered/upload/components/S3Monitor/S3MonitorView";

import CorporateDetailsView from "../registered/corporate_details/CorporateDetailsView";

/**
 * 🛠️ CONFIGURATION
 */
const GLOBAL_ADMIN_ROLE = process.env.REACT_APP_GA || "GLOBALADMIN";
const SITE_ADMIN_ROLE = "SITEADMIN";

/**
 * 🔐 ROUTE WRAPPERS
 */

const RegisteredDashboardRoute = () => (
  <RequireAuth>
    <RegisteredDashboard />
  </RequireAuth>
);

const DataEntryWorkspaceRoute = () => (
  <RequireAuth>
    <DataEntryWorkspace />
  </RequireAuth>
);

const SiteAdminDashboardRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <SiteAdminDashboard />
    </PostSignInAccessGuard>
  </RequireAuth>
);

// 🟡 LEGACY: User Role Assignment (Kept for re-instating)
const UserRoleAssignmentRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <UserRoleAssignmentController onSuccess={() => alert("Identity Database Updated Successfully")}>
        {(ctrl) => (
          <UserRoleAssignmentView
            onAssign={ctrl.assign}
            onHandshake={ctrl.handshake}
            isSubmitting={ctrl.isSubmitting}
            error={ctrl.error}
          />
        )}
      </UserRoleAssignmentController>
    </PostSignInAccessGuard>
  </RequireAuth>
);

// 🟢 NEW: User Management Portal (Refactored Tabbed View)
const SEC_03_ManagementPortalRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <RolePermissionGuard roles={[GLOBAL_ADMIN_ROLE, SITE_ADMIN_ROLE]}>
        <SEC_03_UserManagementMainView />
      </RolePermissionGuard>
    </PostSignInAccessGuard>
  </RequireAuth>
);

const TaxonomyWorkflowRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <WorkflowRefreshESG />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const TaxonomyIngestRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <SustinaTaxonomyView />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const SmartUploadMonitorRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <BatchMonitorView />
    </PostSignInAccessGuard> 
  </RequireAuth>
);

const S3InventoryMonitorRoute = () => {
  const targetBucket = process.env.REACT_APP_SITE_DIRTY_BUCKET;
  const siteKey = process.env.REACT_APP_SITE_KEY;
  const config = { siteKey };

  return (
    <RequireAuth>
      <PostSignInAccessGuard>
        <S3MonitorView targetBucket={targetBucket} config={config} />
      </PostSignInAccessGuard> 
    </RequireAuth>
  );
};

const WorkflowXls2SchemaRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <WorkflowXls2SchemaController />
    </PostSignInAccessGuard>
  </RequireAuth>
);

const WorkflowHubRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard requiredRole={GLOBAL_ADMIN_ROLE}>
      <RolePermissionGuard requiredRole={GLOBAL_ADMIN_ROLE}>
        <WorkflowHub />
      </RolePermissionGuard>
    </PostSignInAccessGuard>
  </RequireAuth>
);

const CorporateDetailsRoute = () => (
  <RequireAuth>
    <PostSignInAccessGuard>
      <CorporateDetailsView />
    </PostSignInAccessGuard>
  </RequireAuth>
);

/**
 * ✅ FINAL ROUTE REGISTRY
 */
export const routes = [
  { path: "/", element: PublicPage },
  { path: "/subscribe", element: SubscribePage },

  // AUTH
  { path: "/auth/register", element: RegisterPage },
  { path: "/auth/sign-in", element: SignInPage },
  { path: "/verify-email", element: VerifyEmailPage },
  { path: "/reset-password", element: ResetPasswordPage },

  // POST-LOGIN HANDOFF
  { path: "/auth/handoff", element: SEC_03_00_PostLoginHandoff },

  // MAIN APP
  { path: "/registered/dashboard", element: RegisteredDashboardRoute },
  { path: "/registered/data-entry", element: DataEntryWorkspaceRoute },
  { path: "/registered/corporate-details", element: CorporateDetailsRoute },

  // ADMIN
  { path: "/registered/site-admin", element: SiteAdminDashboardRoute },
  
  // 🟢 NEW SECURITY PORTAL PATH
  { path: "/registered/site-admin/security-portal", element: SEC_03_ManagementPortalRoute },

  // 🟡 LEGACY PATH (Retained)
  { path: "/registered/site-admin/user-roles", element: UserRoleAssignmentRoute },
  
  { path: "/registered/site-admin/taxonomy-workflow", element: TaxonomyWorkflowRoute },
  { path: "/registered/site-admin/taxonomy", element: TaxonomyIngestRoute },
  { path: "/registered/site-admin/xls-to-schema", element: WorkflowXls2SchemaRoute },
  { path: "/registered/site-admin/workflow_hub", element: WorkflowHubRoute },

  // MONITORING
  { path: "/registered/upload/monitor", element: SmartUploadMonitorRoute },
  { path: "/registered/s3-inventory", element: S3InventoryMonitorRoute },
];